/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { TurboModule } from 'react-native';
import { Platform } from 'react-native';

export type ScreenshotTakenPayload = {
  timestamp: number;
  /**
   * Image path/URI for the overlay. No system thumbnail is used.
   * - iOS: app-captured key window (UIGraphicsImageRenderer), saved as JPEG in temp dir; file path.
   * - Android: file URI of the host app's PixelCopy capture of its own window (API 34+).
   */
  screenshotUri?: string;
};

/**
 * ScreenshotDetector Turbo Module spec. Only the Turbo Module is used (no legacy bridge).
 * Null on web or when the module is not registered by the host app.
 * Listener is registered with addScreenshotTakenListener; use removeListener(id) to clean up.
 */
export interface NativeScreenshotDetectorSpec extends TurboModule {
  addScreenshotTakenListener(
    callback: (payload: ScreenshotTakenPayload) => void,
  ): string;
  removeListener(listenerId: string): void;
}

/**
 * ScreenshotDetector via Turbo Module only. Native implementation must be provided
 * as a Turbo Module (e.g. by the host app's codegen or NPS package's Turbo Module impl).
 */
const getTurboModuleRegistry = () => {
  if (Platform.OS === 'web') return null;
  const reactNative = require('react-native') as {
    TurboModuleRegistry?: {
      get: <T>(moduleName: string) => T | null;
    };
  };
  return reactNative.TurboModuleRegistry ?? null;
};

const TurboModuleScreenshotDetector =
  getTurboModuleRegistry()?.get<NativeScreenshotDetectorSpec>(
    'ScreenshotDetector',
  );

const NativeScreenshotDetector = TurboModuleScreenshotDetector ?? null;

/** True when the native ScreenshotDetector module is available (iOS/Android). */
export const isScreenshotDetectorAvailable = Boolean(
  NativeScreenshotDetector?.addScreenshotTakenListener,
);

export default NativeScreenshotDetector;
